import {DependencyList, EffectCallback, useEffect, useRef} from 'react';

import {assert} from 'Asserts';

/**
 * This hook lets you respond to changes in a prop or other value. The word "changes"
 * is very specific here: it is not run at startup; only when the value changes.
 *
 * In most cases, you should use `useRef` to lock down a value so it doesn't change.
 * This hook allows you to _know_ about changes, not simply lock them down. For example,
 * with this hook, you can alert the developer that a prop has changed, rather than
 * simply preventing a new value from being used.
 *
 * More specifically: consider a component which depends on its props being persistent
 * throughout its lifecycle. Its contract includes "THOU SHALT NOT CHANGE THIS PROP,
 * cause I need it tyvm." While `useRef` suffices in terms of protecting the data, it
 * does not alert developers about a broken contract. Using `useRef` alone would lead
 * to stale data and a bug that may be hard to diagnose. The hook below allows you to
 * flag a broken contract so the developer knows exactly what went wrong.
 */

export default function useOnChangeOnly(effect: EffectCallback, deps: DependencyList): void {
  const isInitialRender = useRef(true);

  assert(deps.length > 0, 'Dependencies must be provided to `useOnChangeOnly');
  useEffect(() => {
    let cleanup: (() => void) | null | void = null;
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      cleanup = effect();
    }
    return () => {
      if (cleanup != null) {
        cleanup();
      }
    };
    // The dependencies are provided by the caller, and cannot be evaluated here.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
